import React, { useState, useRef } from 'react';
import { X, Wrench, Upload, AlertCircle, CheckCircle2 } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useToast } from '../common/ToastContext';
import { useCreateTicket, useAddTicketAttachment } from '../../hooks/queries/useMaintenance';
import { useTenantScope } from '../../hooks/useTenantScope';
import { useAppStore } from '../../store';
import { MaintenanceTicket } from '@repo/shared-types';
import { logger } from '@repo/shared-utils';
import { useCurrentTenantId } from '../../hooks/queries/useTenantId';
import { newClientEntityId } from '@/utils/ids';

interface MaintenanceRequestFormProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export const MaintenanceRequestForm: React.FC<MaintenanceRequestFormProps> = ({ onClose, onSuccess }) => {
  const { showToast } = useToast();
  const currentUserId = useAppStore(state => state.currentUserId);
  const { data: tenantId } = useCurrentTenantId();
  const currentTenantId = tenantId || currentUserId; // Fallback for backward compatibility
  const { scope } = useTenantScope(currentTenantId);
  const createTicketMutation = useCreateTicket();
  const addAttachmentMutation = useAddTicketAttachment();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'Medium' as 'Low' | 'Medium' | 'High' | 'Critical',
    category: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!scope) {
    return (
      <div className="p-8 text-center text-slate-500">
        <LoadingSpinner className="mx-auto mb-2" />
        Loading...
      </div>
    );
  }

  const { profile, lease } = scope; // eslint-disable-line @typescript-eslint/no-unused-vars

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        showToast('File size must be less than 5MB', 'error');
        return;
      }
      if (!file.type.startsWith('image/')) {
        showToast('Please select an image file', 'error');
        return;
      }
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleRemoveFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!lease?.property?.id) {
      showToast('Unable to determine property. Please contact support.', 'error');
      return;
    }

    try {
      const newTicket: MaintenanceTicket = {
        id: newClientEntityId(),
        title: formData.title.trim(),
        description: formData.description.trim(),
        status: 'Open',
        priority: formData.priority,
        propertyId: lease.property.id,
        unitId: lease.unit?.id,
        tenantId: currentTenantId,
        source: 'Tenant', // Tenant-created ticket
        createdAt: new Date().toISOString().split('T')[0],
        images: [],
        category: formData.category || undefined,
      };

      const created = await createTicketMutation.mutateAsync(newTicket);

      // Upload image as attachment if provided
      if (selectedFile) {
        try {
          await addAttachmentMutation.mutateAsync({
            ticketId: created.id,
            file: selectedFile,
          });
        } catch (error) {
          // Attachment upload failed, but ticket was created
          logger.error('Failed to upload attachment:', error);
        }
      }

      showToast('Maintenance request submitted successfully', 'success');
      onSuccess?.();
      onClose();
    } catch (_error) {
      showToast('Failed to submit maintenance request', 'error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in px-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden animate-slide-up border border-slate-200 dark:border-slate-800">
        <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-900/50">
          <div className="flex items-center">
            <Wrench size={20} className="mr-2 text-brand-600 dark:text-brand-400" />
            <h3 className="font-bold text-slate-900 dark:text-white text-lg">Submit Maintenance Request</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition"
          >
            <X size={20} className="text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Property/Unit Info */}
          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">Property</p>
            <p className="font-medium text-slate-900 dark:text-white">{lease.property?.name || 'Unknown'}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Unit {lease.unit?.id || 'N/A'}</p>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
              Request Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                if (errors.title) setErrors({ ...errors, title: '' });
              }}
              className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition ${errors.title ? 'border-red-300 dark:border-red-700' : 'border-slate-300 dark:border-slate-700'
                }`}
              placeholder="e.g., Leaky faucet in kitchen"
            />
            {errors.title && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center">
                <AlertCircle size={12} className="mr-1" /> {errors.title}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                if (errors.description) setErrors({ ...errors, description: '' });
              }}
              rows={4}
              className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition resize-none ${errors.description ? 'border-red-300 dark:border-red-700' : 'border-slate-300 dark:border-slate-700'
                }`}
              placeholder="Please provide details about the maintenance issue..."
            />
            {errors.description && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center">
                <AlertCircle size={12} className="mr-1" /> {errors.description}
              </p>
            )}
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {formData.description.length} characters (minimum 10)
            </p>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
              Priority
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as 'Low' | 'Medium' | 'High' | 'Critical' })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none"
            >
              <option value="Low">Low - Can wait a few days</option>
              <option value="Medium">Medium - Should be addressed soon</option>
              <option value="High">High - Needs attention today</option>
              <option value="Critical">Critical - Emergency/Urgent</option>
            </select>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
              Category (Optional)
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none"
            >
              <option value="">Select a category...</option>
              <option value="Plumbing">Plumbing</option>
              <option value="Electrical">Electrical</option>
              <option value="HVAC">HVAC</option>
              <option value="Appliances">Appliances</option>
              <option value="General Repair">General Repair</option>
              <option value="Pest Control">Pest Control</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
              Photo (Optional)
            </label>
            {previewUrl ? (
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-lg border border-slate-300 dark:border-slate-700"
                />
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="absolute top-2 right-2 p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-8 text-center cursor-pointer hover:border-brand-500 dark:hover:border-brand-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
              >
                <Upload size={32} className="mx-auto mb-2 text-slate-400" />
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Click to upload a photo
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                  Max 5MB, JPG/PNG only
                </p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200 dark:border-slate-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createTicketMutation.isPending || addAttachmentMutation.isPending}
              className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-medium transition flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(createTicketMutation.isPending || addAttachmentMutation.isPending) ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} className="mr-2" />
                  Submit Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


